
import React, { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from "@/components/ui/button";
import { usePlaylistDerivatives } from '@/hooks/usePlaylistDerivatives';
import { VideoMasonryGrid } from '@/components/derivatives/VideoMasonryGrid';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, Info } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";

// Playlist ID for "L'Amour c'est surcoté" - using a correct UUID format
const PLAYLIST_ID = 'playlist-5';

// Initial number of items to show (reduced from 9 to 6)
const INITIAL_ITEMS_TO_SHOW = 6;

// Number of additional items to load when clicking "Show More" (changed to 6)
const LOAD_MORE_COUNT = 6;

export const PressCreationsSection = () => {
  const { language } = useLanguage();
  const [visibleItemsCount, setVisibleItemsCount] = useState(INITIAL_ITEMS_TO_SHOW);
  
  // Use a lower limit to reduce potential issues with large data fetches
  const { 
    derivatives, 
    isLoading, 
    error,
    hasMore 
  } = usePlaylistDerivatives(PLAYLIST_ID, 100);
  
  const loadMore = () => {
    setVisibleItemsCount(prevCount => prevCount + LOAD_MORE_COUNT);
  };
  
  // Ensure we don't try to slice an undefined array
  const visibleItems = derivatives && derivatives.length > 0 
    ? derivatives.slice(0, visibleItemsCount) 
    : [];
    
  // Add some console logs for debugging
  console.log('PressCreationsSection - Playlist ID:', PLAYLIST_ID);
  console.log('PressCreationsSection - Loading:', isLoading);
  console.log('PressCreationsSection - Error:', error);
  console.log('PressCreationsSection - Derivatives count:', derivatives?.length || 0);
  
  return (
    <section className="py-12 px-4">
      <div className="container mx-auto max-w-4xl">
        <h2 className="text-2xl md:text-3xl font-bold mb-6">
          ✂️ {language === 'fr' ? 'Ce qu\'ils en ont fait' : 'What they made of it'}
        </h2>
        
        <div className="prose prose-lg dark:prose-invert max-w-none mb-10">
          <p>
            {language === 'fr' ? (
              "Ces vidéos ont été réalisées par les créateurs sélectionnés par Recut, dans le cadre du challenge."
            ) : (
              "These videos were created by the creators selected by Recut, as part of the challenge."
            )}
          </p>
          <p>
            {language === 'fr' ? (
              "Elles incarnent une nouvelle manière d'interagir avec le cinéma : réinterprétation, mise en scène, langage TikTok, format expressif."
            ) : (
              "They embody a new way of interacting with cinema: reinterpretation, staging, TikTok language, expressive format."
            )}
          </p>
          <p>
            {language === 'fr' ? (
              "Chaque contenu a été publié sur les réseaux sociaux dans le respect de la licence."
            ) : (
              "Each content has been published on social networks in compliance with the license."
            )}
          </p>
          <p>
            {language === 'fr' ? (
              <>👉 Vous pouvez <strong>embed</strong> ces créations dans vos articles via <strong>TikTok embed</strong>, 
              <br/>ou les <strong>citer</strong> avec lien et mention du créateur.</>
            ) : (
              <>👉 You can <strong>embed</strong> these creations in your articles via <strong>TikTok embed</strong>, 
              <br/>or <strong>cite</strong> them with a link and mention of the creator.</>
            )}
          </p>
        </div>
        
        {/* Derivatives Grid Display */}
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {Array.from({ length: INITIAL_ITEMS_TO_SHOW }).map((_, index) => (
              <Skeleton key={index} className="aspect-[9/16] rounded-lg" />
            ))}
          </div>
        ) : error ? (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {language === 'fr' 
                ? `Une erreur s'est produite lors du chargement des créations: ${error}` 
                : `An error occurred while loading the creations: ${error}`
              }
            </AlertDescription>
          </Alert>
        ) : visibleItems.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">
              {language === 'fr' 
                ? "Aucune création n'est disponible pour le moment." 
                : "No creations are available at the moment."
              }
            </p>
          </div>
        ) : (
          <>
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-xl font-semibold flex items-center gap-2">
                  {language === 'fr' ? 'Créations originales' : 'Original Creations'}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-help inline-flex">
                          <Info className="h-5 w-5 text-primary/80" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs p-3 bg-popover text-popover-foreground">
                        <p>
                          ❗ {language === 'fr' 
                            ? "Téléchargement, modification ou rediffusion hors TikTok interdits."
                            : "Downloading, modification or rebroadcasting outside of TikTok prohibited."}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </h3>
              </div>
              
              <VideoMasonryGrid 
                items={visibleItems}
                forceVerticalLayout={true}
                isLoading={false}
                error={null}
                columnCount={3} // Set to display 3 columns
              />
            </div>
            
            {visibleItemsCount < (derivatives?.length || 0) && (
              <div className="flex justify-center mt-8">
                <Button 
                  onClick={loadMore} 
                  variant="default"
                  size="lg"
                  className="min-w-[200px]"
                >
                  {language === 'fr' ? 'Voir plus' : 'Show more'}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};
